/* eslint-disable react/no-array-index-key */
/* eslint-disable no-unused-vars */
import React, { FC } from "react";
import { useSelector } from "react-redux";
import Accordion from "../../../components/Accordion/Accordion";
import "./NewReview.css";
import deleteIcon from "../../../assets/icon-bin.svg";
import { CnpCaptureStates, CnpForm, IProduct } from "../../../types";
import { getTotalAmountForLineItemArray, getTotalVatAmountForLineItemArray } from "../../../utils/Calculation";
import errorIcon from "../../../assets/icon-error.svg";
import { numberWithSpaces } from "../../../utils/utils";
import { getFromSessionStorage, StorageKeys } from "../../../utils/Storage";

type LineItemData = {
  lineItems: Array<IProduct>;
};

interface NewReviewProps {
  data: CnpForm;
  updateData: (fields: Partial<LineItemData>) => void;
  canProceed: any;
  goTo: any;
}

const months = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const NewReview: FC<NewReviewProps> = ({ data, updateData, canProceed, goTo }) => {
  const merchantDetails = useSelector(
    // @ts-ignore
    (state) => state.authenticationReducer.merchantDetails,
  );
  const { customerInformation: customer } = data;
  const { complexBuilding, streetAddress, suburb, city, province, country, postalCode } = customer.billingAddress;

  const customerDetailsAvailable = () => {
    return !complexBuilding && !streetAddress && !suburb && !city && !province && !country && !postalCode;
  };

  const { lineItems } = data;
  const { dueDate, invoiceNumber, additionalNotes } = data.paymentTerms;
  const invoiceDueDate = new Date(dueDate);
  const invoiceInitDate = new Date();
  const { companyName } = merchantDetails;
  const shorthand = companyName.toUpperCase().substring(0, 3);

  if (lineItems.length > 0) canProceed(true);
  else canProceed(false);

  const removeItem = (id: number) => {
    const filteredItems = lineItems.filter((item) => item.id !== id);
    updateData({ lineItems: filteredItems });
  };

  const total = getTotalAmountForLineItemArray(lineItems);
  const vat = getTotalVatAmountForLineItemArray(lineItems);
  return (
    <div className="review">
      <div className="mobile">
        {/* accordion for the merchant details */}
        <Accordion header="Merchant Details" allowEdit={false} goToStep={() => null}>
          <div className="merchant-details">
            <h3 className="merchant-trading-name">{merchantDetails.companyName}</h3>
            <div className="merchant-email">{merchantDetails.email}</div>
            <div className="merchant-phone">{merchantDetails.phoneNumber}</div>
            <div className="merchant-address">
              {`${merchantDetails.address.street1 ? `${merchantDetails.address.street1},` : ""} ${
                merchantDetails.address.street2 ? `,${merchantDetails.address.street2},` : ""
              }
              ${merchantDetails.address.province ? `${merchantDetails.address.province},` : ""} ${
                merchantDetails.address.city ? `${merchantDetails.address.city},` : ""
              } ${merchantDetails.address.code ? `${merchantDetails.address.code},` : ""}`}
            </div>
            <br />
            <div className="merchant-invoice">
              <div className="label">Invoice Number</div>
              <div className="value">{invoiceNumber || getFromSessionStorage(StorageKeys.INVOICE_REFERENCE)}</div>
            </div>
            {merchantDetails.vatNumber && (
              <div className="merchant-vat-number">
                <div className="label">VAT Number</div>
                <div className="value">{merchantDetails.vatNumber}</div>
              </div>
            )}
            <div className="merchant-invoice-date">
              <div className="label">Invoice Date</div>
              <div className="value">{`${invoiceInitDate.getDate()} ${
                months[invoiceInitDate.getMonth()]
              } ${invoiceInitDate.getFullYear()}`}</div>
            </div>
          </div>
        </Accordion>
        {/* accordion for the line items */}
        {/* index 1 corresponds to order in form-config.tsx */}
        <Accordion header="Line Items" allowEdit goToStep={() => goTo(4)}>
          {lineItems.length === 0 && (
            <div className="error-container mt-4">
              <img src={errorIcon} alt="Error Icon" />
              <div className="d-flex wrap">
                <div>Please</div>
                <button type="button" onClick={() => goTo(4)}>
                  add line item
                </button>
                <div>in order to share invoice.</div>
              </div>
            </div>
          )}
          {lineItems.map((item, index) => (
            <div className="each-item" key={index}>
              <div className="top-part">
                <div className="rowOne">
                  <div className="name">{item.itemName}</div>
                  <div className="deleteIcon">
                    <img
                      src={deleteIcon}
                      alt="Delete Icon"
                      onClick={() => removeItem(item.id)}
                      // eslint-disable-next-line jsx-a11y/no-noninteractive-element-to-interactive-role
                      role="button"
                      tabIndex={0}
                      onKeyDown={() => {}}
                    />
                  </div>
                </div>
                <div className="rowTwo">{item.description}</div>
              </div>
              <div className="rowThree">
                <div className="quantity">Qty: {numberWithSpaces(item.lineItemQuantity)}</div>
                <div className="price">R {numberWithSpaces(total.toFixed(2))} (incl. VAT)</div>
              </div>
            </div>
          ))}
          <div className="totals">
            <div className="top-part">
              <div className="subtotal">
                <div className="label">Subtotal</div>
                <div className="value">R {numberWithSpaces((total - vat).toFixed(2))}</div>
              </div>
              <div className="vat">
                <div className="label">VAT (15%)</div>
                <div className="value">R {numberWithSpaces(vat.toFixed(2))}</div>
              </div>
            </div>
            <div className="divider" />
            <div className="total">
              <div className="label">Total Due</div>
              <div className="value">R {numberWithSpaces(total.toFixed(2))}</div>
            </div>
          </div>
        </Accordion>
        {/* accordion for the invoice payment terms */}
        <Accordion header="Payment Terms" allowEdit goToStep={() => goTo(5)}>
          <div className="payment-terms">
            <div className="due-date">
              <div className="label">Due Date</div>
              <div className="value">{`${invoiceDueDate.getDate()} ${
                months[invoiceDueDate.getMonth()]
              } ${invoiceDueDate.getFullYear()}`}</div>
            </div>
            {additionalNotes && (
              <div className="additional-notes">
                <div className="label">Additional Notes</div>
                <div className="value">{additionalNotes}</div>
              </div>
            )}
          </div>
        </Accordion>
        {/* accordion for the invoice payment terms */}
        <Accordion header="Customer Details" allowEdit goToStep={() => goTo(6)}>
          <div className="customer-details">
            <div className="name">
              <div className="label">Customer Name</div>
              <div className="value">{customer.customerName}</div>
            </div>
            <div className="email">
              <div className="label">Email</div>
              <div className="value">{customer.emailAddress}</div>
            </div>
            {customer.contactNumber && (
              <div className="phone">
                <div className="label">Cell Number</div>
                <div className="value">{customer.contactNumber}</div>
              </div>
            )}
            {!customerDetailsAvailable() && (
              <div className="address">
                <div className="label">Street Address</div>
                <div className="value">{`${complexBuilding} ${streetAddress} ${suburb}
              ${city} ${province} ${country} ${postalCode}`}</div>
              </div>
            )}
          </div>
        </Accordion>
      </div>
    </div>
  );
};

export default NewReview;
